/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrder } from 'redux/actions/orderActions';
import { getCurrencyCodeForCountry, getPriceByDate } from 'utils/utils';
import { parseOrderCurrency, parseOrderJson } from 'utils/currencyParser';

const dlog = (...args) => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

/**
 * useOrders — centralized hook for the Orders page.
 *
 * Handles:
 *   - Initial fetch + refresh
 *   - Currency rate fetching (parallelized)
 *   - Filtering (search, status, date range)
 *   - Row selection state (for bulk actions)
 *   - Hydrating each order with parsed currency + safe JSON
 *
 * Returns everything the Orders page UI needs as a flat object.
 */
const useOrders = () => {
    const dispatch = useDispatch();
    const { orders, loading, error } = useSelector((s) => s.orderReducer);
    const userLocation = useSelector((s) => s.userLocationReducer);

    const currencySymbol = getCurrencyCodeForCountry(
        userLocation?.location?.country_code ?? 'NG'
    );

    /* Filters */
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    /* Selection (for bulk export) */
    const [selectedIds, setSelectedIds] = useState(new Set());

    /* Currency rates by date */
    const [currencyRates, setCurrencyRates] = useState({});

    /* ─── Initial fetch ─── */
    useEffect(() => {
        dispatch(fetchOrder());
    }, [dispatch]);

    const refresh = useCallback(() => {
        dispatch(fetchOrder());
    }, [dispatch]);

    /* ─── Hydrate orders (parse JSON safely) ─── */
    const rawOrders = useMemo(() => orders?.rows ?? [], [orders]);

    const hydratedOrders = useMemo(() => {
        return rawOrders
            .filter((order) => order != null)
            .map((order) => {
                const orderDetail = parseOrderJson(order.order_details, []);
                const shipmentDetail = parseOrderJson(order.shipment_details, {});
                const currency = parseOrderCurrency(order);

                return {
                    ...order,
                    _orderDetail: orderDetail,
                    _shipmentDetail: shipmentDetail,
                    _currency: currency,
                    _itemCount: Array.isArray(orderDetail) ? orderDetail.length : 0,
                };
            });
    }, [rawOrders]);

    /* ─── Fetch currency rates in parallel ─── */
    useEffect(() => {
        if (!hydratedOrders.length) return;

        let cancelled = false;
        const uniqueDates = [...new Set(
            hydratedOrders.map((o) => o.updatedAt).filter(Boolean)
        )];
        if (uniqueDates.length === 0) return;

        Promise.all(
            uniqueDates.map(async (date) => {
                try {
                    const rate = await getPriceByDate(date, currencySymbol);
                    return [date, rate];
                } catch (err) {
                    dlog('[useOrders] rate fetch failed for', date, err);
                    return [date, null];
                }
            })
        ).then((results) => {
            if (cancelled) return;
            const next = {};
            results.forEach(([date, rate]) => { next[date] = rate; });
            setCurrencyRates(next);
        });

        return () => { cancelled = true; };
    }, [hydratedOrders, currencySymbol]);

    /* ─── Apply filters ─── */
    const filteredOrders = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const start = startDate ? new Date(startDate).getTime() : null;
        const end = endDate ? new Date(endDate + 'T23:59:59').getTime() : null;

        return hydratedOrders.filter((order) => {
            /* Search by order_ref, order_id, customer email */
            if (term) {
                const ref = String(order.order_ref ?? '').toLowerCase();
                const id  = String(order.order_id  ?? '').toLowerCase();
                const email = String(
                    order._shipmentDetail?.delivery_address?.email ?? ''
                ).toLowerCase();
                if (!ref.includes(term) && !id.includes(term) && !email.includes(term)) {
                    return false;
                }
            }

            /* Status filter */
            if (statusFilter && (order.status ?? '').toLowerCase() !== statusFilter) {
                return false;
            }

            /* Payment filter */
            if (paymentFilter && (order.payments ?? '').toLowerCase() !== paymentFilter) {
                return false;
            }

            /* Date range filter (uses createdAt) */
            if (start || end) {
                const ts = order.createdAt ? new Date(order.createdAt).getTime() : null;
                if (!ts) return false;
                if (start && ts < start) return false;
                if (end && ts > end) return false;
            }

            return true;
        });
    }, [hydratedOrders, searchTerm, statusFilter, paymentFilter, startDate, endDate]);

    /* ─── Active filter pill data ─── */
    const activeFilters = useMemo(() => {
        const list = [];
        if (searchTerm)    list.push({ key: 'search',  label: 'Search',  value: `"${searchTerm}"` });
        if (statusFilter)  list.push({ key: 'status',  label: 'Status',  value: statusFilter });
        if (paymentFilter) list.push({ key: 'payment', label: 'Payment', value: paymentFilter });
        if (startDate)     list.push({ key: 'start',   label: 'From',    value: startDate });
        if (endDate)       list.push({ key: 'end',     label: 'To',      value: endDate });
        return list;
    }, [searchTerm, statusFilter, paymentFilter, startDate, endDate]);

    const hasActiveFilters = activeFilters.length > 0;

    /* ─── Filter clearing ─── */
    const clearFilter = useCallback((key) => {
        switch (key) {
            case 'search':  setSearchTerm(''); break;
            case 'status':  setStatusFilter(''); break;
            case 'payment': setPaymentFilter(''); break;
            case 'start':   setStartDate(''); break;
            case 'end':     setEndDate(''); break;
            default: break;
        }
    }, []);

    const clearAllFilters = useCallback(() => {
        setSearchTerm('');
        setStatusFilter('');
        setPaymentFilter('');
        setStartDate('');
        setEndDate('');
    }, []);

    /* ─── Selection ─── */
    const toggleSelection = useCallback((orderId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback((checked) => {
        if (checked) {
            setSelectedIds(new Set(filteredOrders.map((o) => o.order_id)));
        } else {
            setSelectedIds(new Set());
        }
    }, [filteredOrders]);

    const selectedOrders = useMemo(
        () => filteredOrders.filter((o) => selectedIds.has(o.order_id)),
        [filteredOrders, selectedIds]
    );

    const isAllSelected = filteredOrders.length > 0
        && filteredOrders.every((o) => selectedIds.has(o.order_id));

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    /* Reset selection when filters change (avoid stale selection) */
    useEffect(() => {
        setSelectedIds(new Set());
    }, [searchTerm, statusFilter, paymentFilter, startDate, endDate]);

    return {
        /* Data */
        orders: filteredOrders,
        totalCount: hydratedOrders.length,
        filteredCount: filteredOrders.length,
        loading,
        error,
        currencyRates,
        currencySymbol,

        /* Filters */
        searchTerm,    setSearchTerm,
        statusFilter,  setStatusFilter,
        paymentFilter, setPaymentFilter,
        startDate,     setStartDate,
        endDate,       setEndDate,
        activeFilters,
        hasActiveFilters,
        clearFilter,
        clearAllFilters,

        /* Selection */
        selectedIds,
        selectedOrders,
        isAllSelected,
        toggleSelection,
        toggleSelectAll,
        clearSelection,

        /* Actions */
        refresh,
    };
};

export default useOrders;